import React, {useState} from 'react';
import BudgetPage from '../../components/new-ui/project/project-budget-v3/BudgetPage';
import {createFragmentContainer, graphql} from 'react-relay';
import Util from '../../forecast-app/shared/util/util';
import RatesAndInternalCostPage from '../../components/new-ui/project/project-budget-v2/rates_and_internal_cost_page';
import ExpensesPage from '../../components/new-ui/project/project-budget-v2/expenses_page';
import HelpCenterIcon from '../../components/new-ui/help_center_icon';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {useIntl} from 'react-intl';
import {BudgetContentWrapper, BudgetHeaderWrapper, BudgetWrapper, Tab, Tabs} from './project_budget_v3.styled';
import BudgetOverviewPage from '../../components/new-ui/project/project-budget-v3/BudgetOverviewPage';
import {PROJECT_STATUS} from '../../constants';
import BudgetWarningTab from '../../components/new-ui/project/project-budget-v2/components/budget_warning_tab';
import FinancialCalculationTrigger, {financialCalculationTriggerQuery} from '../../components/new-ui/project/project-budget-v3/loaders/FinancialCalculationTrigger';
import {LoadMore} from '../../components/loaders/LoadMore';


const ProjectBudgetV3 = props => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {availableFeatureFlags} = props.viewer;
	const {modules} = props.viewer.company;

	const hasFinancials = Util.hasFinance(modules);
	const hasBaseline = Util.hasBaselineModule(modules, props.viewer.userType) && props.viewer.project.useBaseline;
	const hasLimitedFinancialAccess = Util.hasLimitedFinancialAccess();

	const tabs = hasFinancials
		? hasLimitedFinancialAccess
			? {
					DETAILS: 'project_budget.details',
					EXPENSES: 'project_budget.expenses',
			  }
			: {
					DETAILS: 'project_budget.details',
					RATES_AND_INTERNAL_COST: 'project_budget.rates_and_internal_cost',
					EXPENSES: 'project_budget.expenses',
			  }
		: {
				DETAILS: 'project_budget.details',
		  };

	const [selectedTab, setSelectedTab] = useState((hasFinancials && localStorage.getItem('project-budget-v3-last-selected-tab')) || tabs.DETAILS);

	const changeTab = tab => {
		setSelectedTab(tab);
	};

	const renderSelectedTab = result => {
		Util.localStorageSetItem('project-budget-v3-last-selected-tab', selectedTab);
		switch (selectedTab) {
			case tabs.OVERVIEW:
				return (
					<BudgetOverviewPage key={'BudgetOverviewPage' + result.timestamp} project={props.viewer.project} company={props.viewer.company} viewer={props.viewer} retry={props.retry} hasFinancials={hasFinancials} hasBaseline={hasBaseline} />
				);
			case tabs.DETAILS:
				return <BudgetPage key={'BudgetPage' + result.timestamp} viewer={props.viewer} project={props.viewer.project} company={props.viewer.company} retry={props.retry} hasFinancials={hasFinancials} hasBaseline={hasBaseline} hasLimitedFinancialAccess={hasLimitedFinancialAccess} />;
			case tabs.RATES_AND_INTERNAL_COST:
				return (
					<RatesAndInternalCostPage key={'RatesAndInternalCostPage' + result.timestamp} project={props.viewer.project} company={props.viewer.company} relay={props.relay} retry={props.retry} availableFeatureFlags={availableFeatureFlags} />
				);
			case tabs.EXPENSES:
				return <ExpensesPage key={'ExpensesPage' + result.timestamp} project={props.viewer.project} company={props.viewer.company} retry={props.retry} availableFeatureFlags={availableFeatureFlags} />;
			default:
				return null;
		}
	};

	const getHeaderTitle = () => {
		const content = [];

		content.push(<HelpCenterIcon key={'help-center-icon'} href={'https://help.forecast.it/project-budget'} />);

		if (props.viewer.project.status !== PROJECT_STATUS.DONE && props.viewer.project.status !== PROJECT_STATUS.HALTED) {
			content.push(
				<div key={'top_header_warning_button'} className="warning-button">
					<BudgetWarningTab
						setSelectedTab={tab => {
							setSelectedTab(tab);
						}}
						hasFinancials={hasFinancials}
						project={props.viewer.project}
						tabs={tabs}
						viewer={props.viewer}
						relay={props.relay}
						retry={props.retry}
					/>
				</div>
			);
		}

		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar key={'budget_v3_top_header_bar'} title={formatMessage({id: 'common.budget'})} content={content} />
			</TopHeaderBarWrapper>
		);
	};

	const allTabs = Object.values(tabs);

	return (
		<LoadMore
			key="financial-calculation-trigger"
			query={financialCalculationTriggerQuery}
			variables={{
				projectId: props.viewer.project.id,
			}}
			componentName="financial_calculation_trigger"
			loader={<FinancialCalculationTrigger />}
		>
			{result => {
				return (
					<BudgetWrapper data-cy={'budget-page'}>
						{props.children} {/* renders the task modal if open */}
						<BudgetHeaderWrapper>{getHeaderTitle()}</BudgetHeaderWrapper>
						<BudgetContentWrapper>
							<Tabs>
								{allTabs.map(tab => (
									<Tab key={tab} className={selectedTab === tab ? ' active' : ''} onClick={() => changeTab(tab)} data-cy={tab.replace('.', '_')} data-userpilot={`${tab}_tab`}>
										{formatMessage({id: tab})}
									</Tab>
								))}
							</Tabs>
							{renderSelectedTab(result)}
						</BudgetContentWrapper>
					</BudgetWrapper>
				);
			}}
		</LoadMore>
	);
};

const projectBudgetV3Query = graphql`
	query projectBudgetV3_Query($projectId: String) {
		viewer {
			actualPersonId
			userType
			project(id: $projectId) {
				id
			}
			component(name: "project_budget_v3", projectId: $projectId)
			...projectBudgetV3_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {projectBudgetV3Query};

export default createFragmentContainer(ProjectBudgetV3, {
	viewer: graphql`
		fragment projectBudgetV3_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
			id
			project(id: $projectId) {
				id
				estimationUnit # warnings
				useBaseline
				projectStartYear # warnings
				projectStartMonth # warnings
				projectStartDay # warnings
				projectEndYear
				projectEndMonth
				projectEndDay
				companyProjectId
				warnings {
					id
					year
					month
					day
					tasks(first: 10000) {
						edges {
							node {
								id
								name
								startDay
								startMonth
								startYear
								deadlineDay
								deadlineMonth
								deadlineYear
								estimateLow
								estimateHigh
								estimateForecast
							}
						}
					}
					personIds
					expenseItems(first: 10000) {
						edges {
							node {
								id
								name
								expenseYear
								expenseMonth
								expenseDay
							}
						}
					}
					timeRegistrations(first: 10000) {
						edges {
							node {
								id
								year
								month
								day
								minutesRegistered
								person {
									id
									fullName
									profilePictureId
									profilePictureDefaultId
								}
								task {
									name
								}
							}
						}
					}
				}
				...BudgetOverviewPage_project
				...BudgetPage_project
				...expensesPage_project
				...ratesAndInternalCostPage_project
			}
			company {
				id
				...BudgetOverviewPage_company
				...BudgetPage_company
				...expensesPage_company
				...ratesAndInternalCostPage_company
				modules {
					moduleType
				}
				roles(first: 10000) {
					# warnings
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	`,
});
